import { PublicKey } from "@solana/web3.js";
import { DripVaultDescriptor } from "../../constants/dripVaults";

export interface DripPdas {
  vaultState: PublicKey;
  shareMint: PublicKey;
  /** Token account holding the vault's deposited principal. */
  reserve: PublicKey;
  /** Null when no owner was supplied. */
  userPosition: PublicKey | null;
}

/**
 * Derives every PDA a Drip instruction needs for one vault.
 *
 * The two instruction families differ only here, so this is the single place to
 * get seeds right:
 *
 *                 legacy ('kysol')          asset ('asset')
 *   vaultState    ["vault_state"]           ["vault_state", mint]
 *   shareMint     ["share_mint"]            ["share_mint", mint]
 *   reserve       ["ky_sol_vault"]          ["reserve", mint]
 *   userPosition  ["user_position", owner]  ["user_position", mint, owner]
 */
export const deriveDripPdas = (
  vault: DripVaultDescriptor,
  programId: PublicKey,
  owner?: PublicKey | null
): DripPdas => {
  const pda = (seeds: Buffer[]) => PublicKey.findProgramAddressSync(seeds, programId)[0];

  if (vault.kind === "kysol") {
    return {
      vaultState: pda([Buffer.from("vault_state")]),
      shareMint: pda([Buffer.from("share_mint")]),
      reserve: pda([Buffer.from("ky_sol_vault")]),
      userPosition: owner ? pda([Buffer.from("user_position"), owner.toBuffer()]) : null,
    };
  }

  const mint = new PublicKey(vault.depositMint).toBuffer();

  return {
    vaultState: pda([Buffer.from("vault_state"), mint]),
    shareMint: pda([Buffer.from("share_mint"), mint]),
    reserve: pda([Buffer.from("reserve"), mint]),
    userPosition: owner
      ? pda([Buffer.from("user_position"), mint, owner.toBuffer()])
      : null,
  };
};
